import type { TipoSlotHorario } from '../../types/models';

export const DIAS_OPTIONS = [
  { value: 'SEGUNDA', label: 'Seg' },
  { value: 'TERCA', label: 'Ter' },
  { value: 'QUARTA', label: 'Qua' },
  { value: 'QUINTA', label: 'Qui' },
  { value: 'SEXTA', label: 'Sex' },
  { value: 'SABADO', label: 'Sáb' },
];

export interface TemplateSlot {
  numeroAula: number;
  horaInicio: string;
  horaFim: string;
  tipo: TipoSlotHorario;
  descricao: string;
}

export const DEFAULT_FORM = {
  nome: '',
  horaAberturaPortoes: '06:40',
  horaEntradaOficial: '07:00',
  toleranciaAtrasoMin: 15,
  horaSaidaOficial: '12:00',
  horaFechamentoPortoes: '12:30',
  diasFuncionamento: ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA'] as string[],
};

export const DEFAULT_TEMPLATE: TemplateSlot[] = [
  { numeroAula: 1, horaInicio: '07:00', horaFim: '07:50', tipo: 'AULA', descricao: '1ª Aula' },
  { numeroAula: 2, horaInicio: '07:50', horaFim: '08:40', tipo: 'AULA', descricao: '2ª Aula' },
  { numeroAula: 3, horaInicio: '08:40', horaFim: '09:30', tipo: 'AULA', descricao: '3ª Aula' },
  { numeroAula: 4, horaInicio: '09:30', horaFim: '09:50', tipo: 'INTERVALO', descricao: 'Recreio' },
  { numeroAula: 5, horaInicio: '09:50', horaFim: '10:40', tipo: 'AULA', descricao: '4ª Aula' },
  { numeroAula: 6, horaInicio: '10:40', horaFim: '11:30', tipo: 'AULA', descricao: '5ª Aula' },
];

export const TURNO_PRESETS: Record<
  string,
  { form: typeof DEFAULT_FORM; template: TemplateSlot[]; icon: string; cor: string; desc: string }
> = {
  manha: {
    icon: '☀️',
    cor: '#f59e0b',
    desc: 'Entrada 07:00 — Saída 12:00 — 5 aulas + recreio',
    form: {
      nome: 'Manhã',
      horaAberturaPortoes: '06:40',
      horaEntradaOficial: '07:00',
      toleranciaAtrasoMin: 15,
      horaSaidaOficial: '12:00',
      horaFechamentoPortoes: '12:30',
      diasFuncionamento: ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA'],
    },
    template: DEFAULT_TEMPLATE,
  },
  tarde: {
    icon: '🌤️',
    cor: '#3b82f6',
    desc: 'Entrada 13:00 — Saída 17:30 — 5 aulas + recreio',
    form: {
      nome: 'Tarde',
      horaAberturaPortoes: '12:30',
      horaEntradaOficial: '13:00',
      toleranciaAtrasoMin: 15,
      horaSaidaOficial: '17:30',
      horaFechamentoPortoes: '18:00',
      diasFuncionamento: ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA'],
    },
    template: [
      { numeroAula: 1, horaInicio: '13:00', horaFim: '13:50', tipo: 'AULA', descricao: '1ª Aula' },
      { numeroAula: 2, horaInicio: '13:50', horaFim: '14:40', tipo: 'AULA', descricao: '2ª Aula' },
      { numeroAula: 3, horaInicio: '14:40', horaFim: '15:30', tipo: 'AULA', descricao: '3ª Aula' },
      { numeroAula: 4, horaInicio: '15:30', horaFim: '15:50', tipo: 'INTERVALO', descricao: 'Recreio' },
      { numeroAula: 5, horaInicio: '15:50', horaFim: '16:40', tipo: 'AULA', descricao: '4ª Aula' },
      { numeroAula: 6, horaInicio: '16:40', horaFim: '17:30', tipo: 'AULA', descricao: '5ª Aula' },
    ],
  },
  noite: {
    icon: '🌙',
    cor: '#6366f1',
    desc: 'Entrada 19:00 — Saída 22:30 — 4 aulas + intervalo',
    form: {
      nome: 'Noite',
      horaAberturaPortoes: '18:30',
      horaEntradaOficial: '19:00',
      toleranciaAtrasoMin: 15,
      horaSaidaOficial: '22:30',
      horaFechamentoPortoes: '23:00',
      diasFuncionamento: ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA'],
    },
    template: [
      { numeroAula: 1, horaInicio: '19:00', horaFim: '19:50', tipo: 'AULA', descricao: '1ª Aula' },
      { numeroAula: 2, horaInicio: '19:50', horaFim: '20:40', tipo: 'AULA', descricao: '2ª Aula' },
      { numeroAula: 3, horaInicio: '20:40', horaFim: '21:00', tipo: 'INTERVALO', descricao: 'Intervalo' },
      { numeroAula: 4, horaInicio: '21:00', horaFim: '21:50', tipo: 'AULA', descricao: '3ª Aula' },
      { numeroAula: 5, horaInicio: '21:50', horaFim: '22:30', tipo: 'AULA', descricao: '4ª Aula' },
    ],
  },
};

export function turnoAccent(nome: string): string {
  const n = nome.toLowerCase();
  if (n.includes('manh') || n.includes('matut')) return '#f59e0b';
  if (n.includes('tard') || n.includes('vesp')) return '#3b82f6';
  if (n.includes('noit') || n.includes('not')) return '#6366f1';
  return '#2563eb';
}

export function addMinutes(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
